import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Search } from 'lucide-react';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import { formatMoney } from '../../components/quote/TotalsPanel';
import './business.css';

const formatWhen = (iso, locale) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const CompaniesPage = ({ storeCurrency = 'USD' }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('');
  const [companies, setCompanies] = useState([]);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const data = await api.listCompanies(q);
      setCompanies(data?.companies || []);
    } catch (err) {
      showToast(err.message || t('business.companies.loadFailed'), 'error');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const timer = setTimeout(() => load(filter), filter ? 250 : 0);
    return () => clearTimeout(timer);
  }, [filter, load]);

  const onSync = async () => {
    setSyncing(true);
    try {
      const res = await api.syncCustomers();
      showToast(
        t('business.companies.synced', { count: res.companies || 0 }),
        'success',
      );
      await load(filter);
    } catch (err) {
      showToast(err.message || t('business.companies.syncFailed'), 'error');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="q2c-biz">
      <div className="q2c-biz-header">
        <h2>{t('business.companies.title')}</h2>
        <Button size="sm" variant="outline-primary" disabled={syncing} onClick={onSync}>
          {syncing ? t('business.companies.syncing') : t('business.companies.syncFromQuotes')}
        </Button>
      </div>

      <div className="q2c-biz-toolbar">
        <div className="q2c-biz-search">
          <Search size={16} />
          <Form.Control
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('business.companies.filterPlaceholder')}
          />
        </div>
      </div>

      <div className="q2c-biz-panel">
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : companies.length === 0 ? (
          <div className="text-muted text-center py-4">
            {t('business.companies.empty')}
          </div>
        ) : (
          <div className="q2c-biz-table-wrap">
            <table className="q2c-biz-table">
              <thead>
                <tr>
                  <th>{t('business.companies.colCompany')}</th>
                  <th>{t('business.companies.colCreated')}</th>
                  <th>{t('business.companies.colCustomers')}</th>
                  <th>{t('business.companies.colQuotes')}</th>
                  <th>{t('business.companies.colTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id}>
                    <td className="fw-semibold">{c.name}</td>
                    <td>{formatWhen(c.createdAt, i18n.language)}</td>
                    <td>{c.customerCount}</td>
                    <td>{c.quoteCount}</td>
                    <td>{formatMoney(c.total, c.currency || storeCurrency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompaniesPage;
